/**
 * The community "theme" customization model for the Studio (the Figma-style
 * editor). The canvas mirrors the real in-community wireframe (channel rail +
 * chat + members) — its STRUCTURE is fixed; the studio only customizes the look
 * (background theme, accent, font, surface) and the content that lives inside the
 * wireframe (community name + icon, channel names + icons). Client-only for now —
 * drafts persist to localStorage. There's no `community_themes` table yet, so
 * saving/sharing a finished theme is a backend gap surfaced in the UI, not faked.
 */
export type CardStyle = 'glass' | 'solid' | 'outline';
export type CornerStyle = 'sharp' | 'soft' | 'round';
export type Density = 'tight' | 'cozy' | 'roomy';
export type FontChoice = 'sans' | 'grotesk' | 'rounded' | 'serif' | 'slab' | 'mono' | 'display';
export type BackdropDim = 'light' | 'medium' | 'dark';
export type NameStyle = 'plain' | 'gradient';
export type RailWidth = 'narrow' | 'normal' | 'wide';
export type MessageStyle = 'flat' | 'bubble';
export type BgTint = 'off' | 'subtle' | 'bold';
export type ChannelKind = 'text' | 'voice';

export type { ChannelEffect } from '@/lib/channelEffects';
import type { ChannelEffect } from '@/lib/channelEffects';
import type { BannerDesign } from './model/bannerLayers';

/** A selectable region of the fixed wireframe (drives the inspector). */
export type Selection = 'page' | 'community' | 'channels' | 'chat' | 'members';

export interface StudioChannel {
  id: string;
  name: string;
  /** Icon id from `@/lib/communityIcons`. */
  icon: string;
  kind: ChannelKind;
  /** Rail group header, e.g. "INFORMATION" / "TEXT CHANNELS" (mirrors the real rail). */
  section: string;
  /** Discord-style decoration that floats above the channel. */
  effect: ChannelEffect;
}

export interface StudioState {
  // Background
  themeId: string;
  customImage: string | null;
  dim: BackdropDim;
  // Brand
  accent: string;
  font: FontChoice;
  // Surface
  cardStyle: CardStyle;
  corners: CornerStyle;
  density: Density;
  // Layout & background
  railWidth: RailWidth;
  messageStyle: MessageStyle;
  bgTint: BgTint;
  vignette: boolean;
  // Effects
  glow: boolean;
  animated: boolean;
  // Community identity
  communityName: string;
  tagline: string;
  nameStyle: NameStyle;
  joinLabel: string;
  /** Rail header banner image (hosted URL); null = accent gradient. */
  banner: string | null;
  /** Banner focal point, percentages 0–100 (drag-to-reframe in every surface). */
  bannerFocal: { x: number; y: number };
  /** Layered Banner Studio design; flattened to `communities.banner` on publish. */
  bannerDesign: BannerDesign | null;
  // Channels (content inside the fixed rail)
  channels: StudioChannel[];
}

export const STUDIO_ACCENTS = [
  '#54b9fe',
  '#a855f7',
  '#22c55e',
  '#f97316',
  '#ef4444',
  '#fbbf24',
  '#ec4899',
  '#14b8a6',
];

export const CARD_STYLES: { id: CardStyle; label: string }[] = [
  { id: 'glass', label: 'Glass' },
  { id: 'solid', label: 'Solid' },
  { id: 'outline', label: 'Outline' },
];

export const CORNER_STYLES: { id: CornerStyle; label: string }[] = [
  { id: 'sharp', label: 'Sharp' },
  { id: 'soft', label: 'Soft' },
  { id: 'round', label: 'Round' },
];

export const DENSITIES: { id: Density; label: string }[] = [
  { id: 'tight', label: 'Tight' },
  { id: 'cozy', label: 'Cozy' },
  { id: 'roomy', label: 'Roomy' },
];

export const FONTS: { id: FontChoice; label: string }[] = [
  { id: 'sans', label: 'Sans' },
  { id: 'grotesk', label: 'Grotesk' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'serif', label: 'Serif' },
  { id: 'slab', label: 'Slab' },
  { id: 'mono', label: 'Mono' },
  { id: 'display', label: 'Display' },
];

/** Inline font-family stack per choice — used to preview each font in its own face. */
export const FONT_STACK: Record<FontChoice, string> = {
  sans: "'Inter', system-ui, sans-serif",
  grotesk: "'Space Grotesk', sans-serif",
  rounded: "'Poppins', sans-serif",
  serif: "'Playfair Display', Georgia, serif",
  slab: "'Roboto Slab', Georgia, serif",
  mono: "'JetBrains Mono', monospace",
  display: "'Arial Black', Impact, sans-serif",
};

export const NAME_STYLES: { id: NameStyle; label: string }[] = [
  { id: 'plain', label: 'Plain' },
  { id: 'gradient', label: 'Gradient' },
];

export const BACKDROP_DIMS: { id: BackdropDim; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'medium', label: 'Medium' },
  { id: 'dark', label: 'Dark' },
];

/** Canvas modes shown in the toolbar above the preview. */
export type StudioMode = 'community' | 'banner';

export const CHANNEL_KINDS: { id: ChannelKind; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'voice', label: 'Voice' },
];

export const RAIL_WIDTHS: { id: RailWidth; label: string }[] = [
  { id: 'narrow', label: 'Narrow' },
  { id: 'normal', label: 'Normal' },
  { id: 'wide', label: 'Wide' },
];

export const MESSAGE_STYLES: { id: MessageStyle; label: string }[] = [
  { id: 'flat', label: 'Flat' },
  { id: 'bubble', label: 'Bubble' },
];

export const BG_TINTS: { id: BgTint; label: string }[] = [
  { id: 'off', label: 'Off' },
  { id: 'subtle', label: 'Subtle' },
  { id: 'bold', label: 'Bold' },
];

export function defaultStudioState(): StudioState {
  return {
    themeId: 'framer',
    customImage: null,
    dim: 'medium',
    accent: STUDIO_ACCENTS[0]!,
    font: 'sans',
    cardStyle: 'glass',
    corners: 'soft',
    density: 'cozy',
    railWidth: 'normal',
    messageStyle: 'flat',
    bgTint: 'off',
    vignette: false,
    glow: true,
    animated: false,
    communityName: 'Your Community',
    tagline: 'A space for your people.',
    nameStyle: 'plain',
    joinLabel: 'Join community',
    banner: null,
    bannerFocal: { x: 50, y: 50 },
    bannerDesign: null,
    channels: [
      { id: 'announcements', name: 'announcements', icon: 'megaphone', kind: 'text', section: 'INFORMATION', effect: 'none' },
      { id: 'general', name: 'general', icon: 'message', kind: 'text', section: 'TEXT CHANNELS', effect: 'none' },
      { id: 'wins', name: 'wins', icon: 'trophy', kind: 'text', section: 'TEXT CHANNELS', effect: 'flames' },
      { id: 'off-topic', name: 'off-topic', icon: 'sparkles', kind: 'text', section: 'TEXT CHANNELS', effect: 'sparkles' },
      { id: 'lounge', name: 'Lounge', icon: 'volume', kind: 'voice', section: 'VOICE CHANNELS', effect: 'none' },
    ],
  };
}

/** localStorage key — generic for a blank theme, or scoped to an owned community. */
function draftKey(communityId?: string): string {
  return communityId ? `winr-studio-draft:${communityId}` : 'winr-studio-draft';
}

export function loadStudioDraft(communityId?: string): StudioState {
  if (typeof window === 'undefined') return defaultStudioState();
  try {
    const raw = localStorage.getItem(draftKey(communityId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StudioState>;
      const base = defaultStudioState();
      // Guard arrays so an older/partial draft can't crash the canvas.
      return {
        ...base,
        ...parsed,
        channels: Array.isArray(parsed.channels) && parsed.channels.length ? parsed.channels : base.channels,
      };
    }
  } catch {
    /* corrupt draft — fall back to defaults */
  }
  return defaultStudioState();
}

export function saveStudioDraft(state: StudioState, communityId?: string): void {
  try {
    localStorage.setItem(draftKey(communityId), JSON.stringify(state));
  } catch {
    /* quota / private mode — best-effort */
  }
}

/** Whether a saved studio draft already exists for this scope. */
export function hasStudioDraft(communityId?: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(draftKey(communityId)) != null;
  } catch {
    return false;
  }
}

const SECTION_RANK = ['INFORMATION', 'TEXT CHANNELS', 'VOICE CHANNELS', 'CHANNELS'];

/** Group channels by their section header, ordered like the real community rail. */
export function groupStudioChannels(channels: StudioChannel[]): { section: string; items: StudioChannel[] }[] {
  const groups: { section: string; items: StudioChannel[] }[] = [];
  for (const ch of channels) {
    const section = ch.section || 'CHANNELS';
    let g = groups.find((x) => x.section === section);
    if (!g) {
      g = { section, items: [] };
      groups.push(g);
    }
    g.items.push(ch);
  }
  const rank = (s: string) => {
    const i = SECTION_RANK.indexOf(s);
    return i === -1 ? 99 : i;
  };
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rank(a.g.section) - rank(b.g.section) || a.i - b.i)
    .map((x) => x.g);
}

/** A fresh unique id for a new channel row. */
export function newChannelId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ch-${performance.now().toString(36)}`;
  }
}

/* ── Derived class helpers (single source of truth for canvas styling) ───────── */

export function cardClass(style: CardStyle): string {
  if (style === 'solid') return 'border border-white/10 bg-[#0c0c12]';
  if (style === 'outline') return 'border-2 border-white/25 bg-black/25';
  return 'border border-white/12 bg-white/[0.07] backdrop-blur-2xl';
}

export function radiusClass(corners: CornerStyle): string {
  return corners === 'sharp' ? 'rounded-none' : corners === 'round' ? 'rounded-[34px]' : 'rounded-2xl';
}

export function rowPadClass(density: Density): string {
  return density === 'tight' ? 'py-1' : density === 'roomy' ? 'py-2.5' : 'py-1.5';
}

export function fontClass(font: FontChoice): string {
  switch (font) {
    case 'grotesk':
      return "[font-family:'Space_Grotesk',sans-serif]";
    case 'rounded':
      return "[font-family:'Poppins',sans-serif]";
    case 'serif':
      return "[font-family:'Playfair_Display',Georgia,serif]";
    case 'slab':
      return "[font-family:'Roboto_Slab',Georgia,serif]";
    case 'mono':
      return 'font-mono';
    case 'display':
      return "[font-family:'Arial_Black',Impact,sans-serif] tracking-[-0.01em]";
    default:
      return 'font-sans';
  }
}

/** Channel-rail column width in px for the canvas grid. */
export function railWidthPx(width: RailWidth): string {
  return width === 'narrow' ? '168px' : width === 'wide' ? '244px' : '200px';
}

/** Accent wash opacity over the background for `bgTint`. */
export function bgTintOpacity(tint: BgTint): number {
  return tint === 'subtle' ? 0.12 : tint === 'bold' ? 0.26 : 0;
}

/** Readability scrim over the background photo, strength per `dim`. */
export function dimOverlay(dim: BackdropDim): string {
  if (dim === 'light') return 'linear-gradient(rgba(4,4,6,0.30), rgba(4,4,6,0.45))';
  if (dim === 'dark') return 'linear-gradient(rgba(4,4,6,0.72), rgba(4,4,6,0.86))';
  return 'linear-gradient(rgba(4,4,6,0.55), rgba(4,4,6,0.72))';
}
